import { Slider, Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@playcastdotio/Shadcn';
import { useEffect, useSyncExternalStore, useState, useCallback, memo } from 'react';
import { gamepadStore } from './store';
import { faEye, faEyeSlash, faFloppyDisk, faRotateLeft, faX } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';

const BUTTON_LABELS: { [key: string]: string } = {
  dpad: 'D-Pad',
  faceButtons: 'Face Buttons (A,B,X,Y)',
  leftThumbstick: 'Left Thumbstick',
  rightThumbstick: 'Right Thumbstick',
  leftBumper: 'Left Bumper',
  rightBumper: 'Right Bumper',
  leftTrigger: 'Left Trigger',
  rightTrigger: 'Right Trigger',
  leftMeta: 'Left Meta',
  rightMeta: 'Right Meta',
  leftStick: 'Left Stick',
  rightStick: 'Right Stick',
};

const ButtonVisibilityControl = memo(() => {
  const hiddenButtons = useSyncExternalStore(gamepadStore.subscribeHiddenButtons, gamepadStore.getHiddenButtons);
  const isSidebarOpen = useSyncExternalStore(gamepadStore.subscribeAdvanceSettings, gamepadStore.getIsAdvanceSettingsGamepad);
  const [selectedButton, setSelectedButton] = useState<string>(Object.keys(BUTTON_LABELS)[0]);
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  // Reset selected button and close dropdown when sidebar closes
  useEffect(() => {
    if (!isSidebarOpen) {
      setSelectedButton(Object.keys(BUTTON_LABELS)[0]);
      setIsSelectOpen(false);
    }
  }, [isSidebarOpen]);

  return (
    <div>
      <label className='text-sm text-white/70'>Button Visibility</label>
      <div className='flex items-center gap-2'>
        <Select value={selectedButton} onValueChange={setSelectedButton} open={isSelectOpen} onOpenChange={setIsSelectOpen}>
          <SelectTrigger className='w-full border-white/20 bg-black text-white'>
            <SelectValue placeholder='Select a button' />
          </SelectTrigger>
          <SelectContent
            className='z-[100003] max-h-[160px] overflow-y-auto border-white/20 bg-black text-white'
            position='popper'
            sideOffset={4}
          >
            {Object.entries(BUTTON_LABELS).map(([key, label]) => (
              <SelectItem
                key={key}
                value={key}
                className='cursor-pointer text-white hover:bg-white/10 focus:bg-white/10 data-[highlighted]:bg-white/20 data-[state=checked]:bg-amber-400/20 data-[state=checked]:text-amber-400'
              >
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant={hiddenButtons[selectedButton] ? 'outline' : 'default'}
          className={clsx(
            'm-0',
            hiddenButtons[selectedButton] ? 'border-white/20 text-white hover:bg-white/10' : 'bg-amber-400 text-black hover:bg-amber-500',
          )}
          onClick={() => gamepadStore.setButtonHidden(selectedButton, !hiddenButtons[selectedButton])}
          size='icon'
        >
          {hiddenButtons[selectedButton] ? <FontAwesomeIcon icon={faEyeSlash} /> : <FontAwesomeIcon icon={faEye} />}
        </Button>
      </div>
      <span className='text-xs text-white/50'>Select a button to show or hide it from the gamepad</span>
    </div>
  );
});

ButtonVisibilityControl.displayName = 'ButtonVisibilityControl';

export const AdvanceSettingsSidebar = () => {
  const isOpen = useSyncExternalStore(gamepadStore.subscribeAdvanceSettings, gamepadStore.getIsAdvanceSettingsGamepad);
  const [currentOpacity, setCurrentOpacity] = useState(gamepadStore.getButtonOpacity());

  const handleClose = useCallback(() => {
    gamepadStore.setButtonOpacity(gamepadStore.getButtonOpacity());
    gamepadStore.setIsAdvanceSettingsGamepad(false);
  }, []);

  const handleReset = useCallback(() => {
    gamepadStore.resetButtonOpacity();
    const newOpacity = gamepadStore.getButtonOpacity();
    setCurrentOpacity(newOpacity);
    gamepadStore.resetHiddenButtons();
  }, []);

  const handleSave = useCallback(() => {
    gamepadStore.setButtonOpacity(currentOpacity);
    gamepadStore.setIsAdvanceSettingsGamepad(false);
  }, [currentOpacity]);

  const handleOpacityChange = useCallback((value: number[]) => {
    const opacity = value[0];
    setCurrentOpacity(opacity);
    gamepadStore.setButtonOpacity(opacity);
  }, []);

  const handleOpacityCommit = useCallback((value: number[]) => {
    const opacity = value[0];
    setCurrentOpacity(opacity);
  }, []);

  useEffect(() => {
    if (isOpen) {
      const savedOpacity = gamepadStore.getButtonOpacity();
      setCurrentOpacity(savedOpacity);
    }
  }, [isOpen]);

  return (
    <>
      <div
        className={clsx(
          'fixed inset-0 z-[100000] bg-black/20 transition-opacity duration-300',
          isOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={(e) => {
          // Only close if the click target is the backdrop itself
          if (e.target === e.currentTarget) {
            handleClose();
          }
        }}
      />
      <div
        className={clsx(
          'fixed right-0 top-0 z-[100001] h-full w-[300px] transform overflow-y-auto bg-black/80 p-6 shadow-lg transition-transform duration-300 ease-in-out',
          isOpen ? 'translate-x-0' : 'translate-x-full',
        )}
        style={
          {
            '--slider-transition': 'opacity 150ms ease-out',
            pointerEvents: isOpen ? 'auto' : 'none',
            touchAction: 'pan-y',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(255, 255, 255, 0.2) transparent',
          } as React.CSSProperties
        }
        onTouchStart={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <div className='flex items-center justify-between border-b border-white/20 pb-4'>
          <h2 className='font-[Syne] text-lg font-semibold text-white'>Advanced Settings</h2>
          <Button variant='ghost' className='h-8 w-8 p-0 text-white hover:bg-white/10' onClick={handleClose}>
            <FontAwesomeIcon icon={faX} className='h-4 w-4' />
          </Button>
        </div>

        <div className='mt-6 flex flex-col gap-6'>
          <div className='flex flex-col gap-2'>
            <label className='text-sm text-white/70'>Button Opacity</label>
            <Slider
              value={[currentOpacity]}
              max={100}
              step={1}
              className='w-full'
              onValueChange={handleOpacityChange}
              onValueCommit={handleOpacityCommit}
            />
            <span className='text-xs text-white/50'>Adjust the opacity of all gamepad buttons</span>
          </div>

          <ButtonVisibilityControl />

          <div className='flex-1' />

          <div className='flex flex-col gap-2'>
            <Button
              variant='outline'
              className='m-0 flex items-center gap-2 border-white/20 text-white hover:bg-white/10'
              onClick={handleReset}
            >
              <FontAwesomeIcon icon={faRotateLeft} className='text-xs' />
              Reset
            </Button>
            <Button className='m-0 flex items-center gap-2 bg-amber-400 text-black hover:bg-amber-500' onClick={handleSave}>
              <FontAwesomeIcon icon={faFloppyDisk} className='text-xs' />
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

